import { computed, onMounted, ref } from 'vue';

import { loadBrandPay } from '@tosspayments/brandpay-sdk';
import type { BrandPayInstance as BrandpayInstance } from '@tosspayments/brandpay__types';

import { addCardNew, fetchAgreementTerms } from '@/api/apiService';
import api from '@/api/index';
import { useModalCardManage } from '@/composables/domains/self-service/useModalCardManage';
import { useAdminStore } from '@/stores/admin';
import { IResponsePaymentMethod } from '@/types/response';

let instance = null as ReturnType<typeof useBrandPay> | null;

// 싱글톤 패턴 강제
export const useBrandPay = () => {
  if (!instance) {
    instance = createBrandPayInstance();
  }
  return instance;
};

// 생성자
const createBrandPayInstance = () => {
  const adminStore = useAdminStore();
  const { setIsHiddenModalCardManage } = useModalCardManage();

  const brandpay = ref<BrandpayInstance>();
  const listPaymentMethod = ref<IResponsePaymentMethod[]>([]); // 결제수단 목록
  const idPaymentMethodSelected = ref(''); // 선택된 결제수단 아이디
  const userAgreementBilling = ref([]);
  const isProd = window.location.origin === 'https://sconchat.sconai.com';
  const clientKey = isProd ? 'live_ck_Z1aOwX7K8mzdamZvxkb93yQxzvNP' : 'test_ck_6BYq7GWPVvexRZMvOPya3NE5vbo1'; // 테스트용 클라이언트 키

  // 자동결제에 동의하였는지
  const isAgreedBilling = computed(() => userAgreementBilling.value.length === 0);

  // 브랜드페이 API 초기화
  const initBrandPay = async () => {
    if (!adminStore.getPartnerEmployeeInfo.userId) {
      await adminStore.fetchPartnerHome();
    }
    brandpay.value = await loadBrandPay(clientKey, adminStore.getPartnerEmployeeInfo.userId, {
      redirectUrl: `${api.defaults.baseURL}/api/v1/selfservice/payment/auth`,
      windowTarget: 'iframe',
    });
  };

  const addPaymentMethod = async (callbackEnd?: () => void, callbackCancel?: () => void) => {
    if (!brandpay.value) {
      await initBrandPay();
    }

    setIsHiddenModalCardManage(true);
    brandpay
      .value!.addPaymentMethod('카드')
      .then(async () => {
        await addCardNew('TOSS');
        callbackEnd?.();
      })
      .catch(function (error) {
        callbackCancel?.();
        console.debug('ERR', error);
      })
      .finally(() => {
        setIsHiddenModalCardManage(false);
      });
  };

  const requestAgreement = (callbackSuccess: () => void, callbackCancel: () => void) => {
    if (!brandpay.value) return;
    brandpay.value
      .requestAgreement('빌링') // 자동결제 선택 약관 동의 호출
      .then(async () => {
        // 성공 처리
        getStatusAgreement();
        callbackSuccess?.();
      })
      .catch(function () {
        // 사용자가 창을 닫아 취소한 경우에 대한 처리
        callbackCancel();
      });
  };

  // 사용자의 자동결제 이용약관 조회
  const getStatusAgreement = async () => {
    const { data: agreementBilling } = await fetchAgreementTerms('TOSS');
    userAgreementBilling.value = agreementBilling;
  };

  onMounted(async () => {
    await initBrandPay();
  });

  return {
    listPaymentMethod,
    idPaymentMethodSelected,
    isAgreedBilling,
    addPaymentMethod,
    requestAgreement,
    getStatusAgreement,
  };
};
